'use client';

import { useEffect, useEffectEvent, useRef } from 'react';

import type { Subject } from 'rxjs';

import {
  ProjectResponseModeEvent,
  useMobileUtSessionGateway,
} from '@/shared-survey';
import {
  endMobileSessionWhenOver,
  linkMobileAudioForInsight,
  startMobileListeningOnRecording,
  syncMobileRecordingTimestamp,
} from '@/shared-utilities';

import { useRemoteVoiceSubscription } from './project-usability-remote-voice.hooks';

interface UseMobileUtIntegrationOptions {
  isMobileMode: boolean;
  mobileUniqueId: string | null;
  insightId: string | null;
  isRecording: boolean;
  isSessionOver: boolean;
  isFinalizingRecording: boolean;
  isVoiceCommandBlocked: boolean;
  ingestTranscript: (transcript: string, isFinal: boolean) => void;
  eventsObservable: Subject<ProjectResponseModeEvent>;
  startListening: (options?: { skipLocalMic?: boolean }) => Promise<void>;
  getCompletionRedirectUrl: () => string | null;
}

export function useMobileUtIntegration({
  isMobileMode,
  mobileUniqueId,
  insightId,
  isRecording,
  isSessionOver,
  isFinalizingRecording,
  isVoiceCommandBlocked,
  ingestTranscript,
  eventsObservable,
  startListening,
  getCompletionRedirectUrl,
}: UseMobileUtIntegrationOptions): void {
  const mobileListeningStartedRef = useRef(false);
  const mobileAudioLinkedRef = useRef(false);
  const mobileSessionEndedRef = useRef(false);
  const recordingSyncedRef = useRef(false);
  const hasFinalizedRecordingRef = useRef(false);

  const { syncRecordingStarted, linkAudio, endSession } =
    useMobileUtSessionGateway();

  useRemoteVoiceSubscription({
    uniqueId: mobileUniqueId,
    ingestTranscript,
    eventsObservable,
    enabled: isMobileMode,
    isBlocked: isVoiceCommandBlocked,
  });

  const readCompletionRedirectUrl = useEffectEvent(getCompletionRedirectUrl);

  useEffect(() => {
    startMobileListeningOnRecording({
      isMobileMode,
      isRecording,
      startedRef: mobileListeningStartedRef,
      startListening,
    });
  }, [isMobileMode, isRecording, startListening]);

  useEffect(() => {
    linkMobileAudioForInsight({
      isMobileMode,
      insightId,
      mobileUniqueId,
      linkedRef: mobileAudioLinkedRef,
      linkMobileAudio: (linkedInsightId, uniqueId) =>
        linkAudio({ insightId: linkedInsightId, uniqueId }),
    });
  }, [isMobileMode, insightId, mobileUniqueId, linkAudio]);

  useEffect(() => {
    syncMobileRecordingTimestamp({
      isMobileMode,
      isRecording,
      mobileUniqueId,
      syncedRef: recordingSyncedRef,
      syncTimestamp: (uniqueId) => syncRecordingStarted(uniqueId),
    });
  }, [isMobileMode, isRecording, mobileUniqueId, syncRecordingStarted]);

  useEffect(() => {
    endMobileSessionWhenOver({
      isMobileMode,
      isSessionOver,
      isFinalizingRecording,
      mobileUniqueId,
      hasFinalizedRef: hasFinalizedRecordingRef,
      sessionEndedRef: mobileSessionEndedRef,
      endMobileSession: (uniqueId) =>
        endSession({
          uniqueId,
          reason: 'conversation_over',
          redirectUrl: readCompletionRedirectUrl(),
        }),
    });
  }, [
    isMobileMode,
    isSessionOver,
    isFinalizingRecording,
    mobileUniqueId,
    endSession,
  ]);
}
